import { Terminal, Pencil, Pen, Zap } from 'lucide-react';

// Helper function to create icon with theme color
const createIcon = (IconComponent, iconColor) => (
  <IconComponent className={`${iconColor} mb-4`} size={32} />
);

export const translations = (iconColor) => ({
  de: {
    nav: {
      about: "Wer wir sind",
      services: "Was wir bieten",
      projects: "Projekte",
      contact: "Kontakt"
    },
    hero: {
      slogans: [
        { lines: ['Groß denken.', 'Den Rest machen wir.'] },
        { lines: ['Mit Sinn gestalten.', 'Für morgen bauen.'] },
        { lines: ['Deine Vision.', 'Unser Handwerk.'] },
        { lines: ['Erst die Idee.', 'Dann die Umsetzung.'] },
      ],
    },
    about: {
      title: "Unsere Leistungen",
      skills: [
        {
          title: 'Entwicklung',
          icon: createIcon(Terminal, iconColor),
          skills: 'HTML5 • PHP (Laravel, Symfony) • JavaScript/JSX • React (Hooks, Context API) • Next.js (SSR, SSG) • Swift (iOS) • Flutter (Cross-Platform)'
        },
        {
          title: 'Grafikdesign',
          icon: createIcon(Pencil, iconColor),
          skills: 'Photoshop (Retusche) • Illustrator (Logos, Vektoren) • InDesign (Layouts) • After Effects (Motion Graphics)'
        },
        {
          title: 'UI/UX Design',
          icon: createIcon(Pen, iconColor),
          skills: 'Figma • Adobe XD • Sketch • Principle (Animation)'
        },
        {
          title: 'Fotografie',
          icon: createIcon(Zap, iconColor),
          skills: 'Produkt • Portrait • Architektur • Post-Production'
        }
      ]
    },
    services: {
      text1: "Wir sind Spezialisten für digitale Erlebnisse der Extraklasse – von schlanken Landing Pages bis hin zu leistungsstarken Webanwendungen. Unser Know-how umfasst Full-Stack-Entwicklung, modernes UI/UX-Design, digitale Beratung und strategische Planung. Egal ob E-Commerce-Plattformen, Content-Management-Systeme oder individuelle Lösungen: Wir setzen deine Vision mit Präzision und Innovation um.",
      text2: "Unsere Services beinhalten API-Integrationen, SEO-Optimierung, Performance-Steigerung und technische Beratung. Zudem begleiten wir Unternehmen durch die digitale Transformation – mit maßgeschneiderten Softwarelösungen, Cloud-Migrationen und skalierbaren Architekturdesigns. Mit uns bist du bestens gerüstet für die digitale Zukunft."
    },
    projects: {
      title: "Aktuelle Projekte",
      items: [
        {
          title: "RSQDATA",
          description: "Website für RSQDATA: Datenrettung für Festplatte, SSD, RAID & NAS. Klare Conversion-Strecke mit kostenloser Analyse inkl. Dateiliste, verbindlichem Festpreis und No-Data-No-Fee.",
          tech: ["Next.js", "UI/UX", "SEO"],
          link: "https://rsqdata.de"
        },
        {
          title: "LMB Marketing",
          description: "Website für einen Instagram-Werbepartner: native In-Feed-Kampagnen, klare Service-Story und Lead-Pfade für Marken, die Reichweite über Content statt Unterbrechung wollen.",
          tech: ["Next.js", "Branding", "UI/UX"],
          link: "https://lmbmarketing.net"
        },
        {
          title: "Remax Carsearch Redesign",
          description: "Modernes Redesign der Fahrzeugsuche für Remax Cars. Optimierte Benutzerführung und responsive Darstellung für alle Endgeräte.",
          tech: ["FIGMA", "JavaScript", "HTML"],
          link: "https://www.carsearch.de/carsearch/ALL/ALL"
        },
        {
          title: "VOCA - Barrierefreies Kommunikationssystem",
          description: "Ein barrierefreies Kommunikationssystem für temporär sprachunfähige Patienten. Entwickelt für die TH Nürnberg. Ermöglicht Patienten die Kommunikation über intuitive UI-Elemente, Symbolsprache, Augensteuerung und andere assistive Eingabemethoden.",
          tech: ["TypeScript", "Vue"],
          link: "https://ratatosknuernberg.info"
        },
        {
          title: "Logistik Intranet",
          description: "Internes Kommunikationsplattform für eines der größten Logistikunternehmen in Nürnberg. Optimierung von Workflows und Teamzusammenarbeit.",
          tech: ["PHP", "MySQL", "JS/JSX"]
        }
      ],
      teasers: [
        {
          name: "myLingra",
          description: "Therapiebegleitende App mit individualisierten Behandlungsplänen, Offline-Cache und barrierearmem UI — Launch in Vorbereitung."
        },
        {
          name: "Onda Köln",
          description: "Digitale Präsenz für ein modernes Restaurant in Köln — Menü, Reservierung und Storytelling in einer schnellen Mobile Experience."
        }
      ]
    },
    contact: {
      title: "Kontakt",
      imprint: "Impressum",
      privacy: "Datenschutz"
    }
  },
  en: {
    nav: {
      about: "About Us",
      services: "What We Offer",
      projects: "Projects",
      contact: "Contact"
    },
    hero: {
      slogans: [
        { lines: ['Think big.', "We'll handle the digital."] },
        { lines: ['Design that lasts.', 'Not just for today.'] },
        { lines: ['Bold ideas.', 'Precise execution.'] },
        { lines: ['Your ambition.', 'Our build.'] },
      ],
    },
    about: {
      title: "About Us",
      skills: [
        {
          title: 'Development',
          icon: createIcon(Terminal, iconColor),
          skills: 'HTML5 • PHP (Laravel, Symfony) • JavaScript/JSX • React (Hooks, Context API) • Next.js (SSR, SSG) • Swift (iOS) • Flutter (Cross-Platform)'
        },
        {
          title: 'Graphic Design',
          icon: createIcon(Pencil, iconColor),
          skills: 'Photoshop (Retouching) • Illustrator (Logos, Vectors) • InDesign (Layouts) • After Effects (Motion Graphics)'
        },
        {
          title: 'UI/UX Design',
          icon: createIcon(Pen, iconColor),
          skills: 'Figma • Adobe XD • Sketch • Principle (Animation)'
        },
        {
          title: 'Photography',
          icon: createIcon(Zap, iconColor),
          skills: 'Product • Portrait • Architecture • Post-Production'
        }
      ]
    },
    services: {
      text1: "We are specialists for premium digital experiences – from sleek landing pages to powerful web applications. Our expertise includes full-stack development, modern UI/UX design, digital consulting, and strategic planning. Whether it's e-commerce platforms, content management systems, or custom solutions: We turn your vision into reality with precision and innovation.",
      text2: "Our services include API integrations, SEO optimization, performance enhancement, and technical consulting. We also guide companies through digital transformation – with custom software solutions, cloud migrations, and scalable architecture designs. With us, you're well-equipped for the digital future."
    },
    projects: {
      title: "Current Projects",
      items: [
        {
          title: "RSQDATA",
          description: "Website for RSQDATA: data recovery for hard drives, SSDs, RAID & NAS. A clear conversion path with free analysis including a file list, a binding fixed price, and no-data-no-fee.",
          tech: ["Next.js", "UI/UX", "SEO"],
          link: "https://rsqdata.de"
        },
        {
          title: "LMB Marketing",
          description: "Website for an Instagram advertising partner: native in-feed campaigns, a clear services story, and lead paths for brands that want reach through content—not interruption.",
          tech: ["Next.js", "Branding", "UI/UX"],
          link: "https://lmbmarketing.net"
        },
        {
          title: "Remax Carsearch Redesign",
          description: "Modern redesign of the vehicle search for Remax Cars. Optimized user experience and responsive display for all devices.",
          tech: ["FIGMA", "JavaScript", "HTML"],
          link: "https://www.carsearch.de/carsearch/ALL/ALL"
        },
        {
          title: "VOCA - Barrier-Free Communication System",
          description: "A barrier-free communication system for temporarily speech-impaired patients. Developed for TH Nürnberg. Enables patients to communicate through intuitive UI elements, symbol language, eye tracking, and other assistive input methods.",
          tech: ["TypeScript", "Vue"],
          link: "https://ratatosknuernberg.info"
        },
        {
          title: "Logistics Intranet",
          description: "Internal communication platform for one of the largest logistics companies in Nuremberg. Optimization of workflows and team collaboration.",
          tech: ["PHP", "MySQL", "JS/JSX"]
        }
      ],
      teasers: [
        {
          name: "myLingra",
          description: "Therapy companion app with personalized treatment plans, offline cache, and accessible UX — launch in preparation."
        },
        {
          name: "Onda Köln",
          description: "Digital presence for a modern restaurant in Cologne — menu, reservations, and storytelling in a fast mobile experience."
        }
      ]
    },
    contact: {
      title: "Contact",
      imprint: "Legal Notice",
      privacy: "Privacy Policy"
    }
  }
});

